import styled, { css } from "styled-components";
import { theme } from "@styles/theme";

/* ============================================================================
 * Modal "Configurar relatório" (operação) — DS v2, fiel ao DC "Relatorio
 * Portal Senne.dc.html" (modal Configurar): nome/variável, lista de parâmetros
 * em cards expansíveis (header slate-50 + chevron), + Adicionar (tracejado),
 * rodapé Remover (outline error) / Salvar (laranja) e confirmação de remoção.
 * Reusa o GenericModal (chrome/close). Classes preservadas p/ o index.tsx.
 * ==========================================================================*/

export const ModalContentWrapper = styled.div`
  padding: 4px 4px 8px;
`;

export const StyledForm = styled.form<{ $isLoading?: boolean }>`
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      opacity: 0.6;
      pointer-events: none;
    `}
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const Field = styled.div<{ $hidden?: boolean }>`
  display: ${({ $hidden }) => ($hidden ? "none" : "flex")};
  flex-direction: column;
  gap: 6px;

  & > label {
    font-size: 14px;
    font-weight: 600;
    color: ${theme.colors.fg1};
  }

  /* checkbox DS: caixa laranja via accent-color */
  & > input[type="checkbox"] {
    width: 18px;
    height: 18px;
    accent-color: ${theme.colors.primary};
    cursor: pointer;

    & + label {
      cursor: pointer;
      margin-left: 8px;
      font-size: 14px;
      font-weight: 500;
      color: ${theme.colors.fg1};
    }
  }

  /* inputs "crus" (rota) — os <Input>/<Select> compartilhados já vêm DS v2 */
  & > input:not([type="checkbox"]) {
    height: 44px;
    padding: 0 12px;
    border: 1.5px solid ${theme.colors.borderStrong};
    border-radius: ${theme.radii.sm};
    background: ${theme.colors.surface};
    font-size: 15px;
    font-family: inherit;
    color: ${theme.colors.fg1};
    outline: none;
    box-sizing: border-box;
    width: 100%;
    transition: border-color 0.15s, box-shadow 0.15s;

    &:focus {
      border-color: ${theme.colors.primary};
      box-shadow: 0 0 0 3px rgba(236, 103, 38, 0.15);
    }
  }

  /* linhas de checkbox: label ao lado da caixa */
  &:has(> input[type="checkbox"]) {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
`;

export const ParamsSection = styled.div`
  border-top: 1px solid ${theme.colors.border};
  padding-top: 16px;
  display: flex;
  flex-direction: column;

  & > .title {
    font-size: 16px;
    font-weight: 700;
    color: ${theme.colors.fg1};
    margin-bottom: 8px;
  }
`;

export const ParamsList = styled.div`
  overflow: auto;
  max-height: 40vh;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const ParamItem = styled.div<{ $active?: boolean | undefined }>`
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  overflow: hidden;

  & > .title {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    background: ${theme.colors.gray50};
    cursor: pointer;
    user-select: none;
    font-size: 15px;
    font-weight: 600;
    color: ${theme.colors.fg1};

    &::before {
      content: "";
      width: 10px;
      height: 10px;
      border-right: 2px solid ${theme.colors.fg2};
      border-bottom: 2px solid ${theme.colors.fg2};
      transform: ${({ $active }) =>
        $active ? "rotate(45deg)" : "rotate(-45deg)"};
      transition: transform 0.2s;
      flex: none;
      margin-bottom: ${({ $active }) => ($active ? "3px" : "0")};
    }
  }

  & > .fields {
    max-height: ${({ $active }) => ($active ? "900px" : "0px")};
    overflow: hidden;
    transition: max-height 0.4s ease-in-out;
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: ${({ $active }) => ($active ? "16px" : "0 16px")};

    .actions > button {
      width: 100%;
      height: 44px;
      background: transparent;
      border: 1.5px solid ${theme.colors.danger};
      border-radius: ${theme.radii.md};
      color: ${theme.colors.danger};
      font-family: inherit;
      font-size: 15px;
      font-weight: 700;
      cursor: pointer;
      transition: background 0.15s;

      &:hover {
        background: ${theme.colors.dangerSoft};
      }
    }
  }
`;

export const FormActions = styled.div<{ $removeActive?: boolean }>`
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;

  ${({ $removeActive }) =>
    $removeActive
      ? css`
          justify-content: flex-start;
          .submit_button,
          .remove_button {
            display: none;
          }
        `
      : css`
          .confirm_button,
          .cancel_button {
            display: none;
          }
        `}

  button {
    height: 48px;
    padding: 0 20px;
    border-radius: ${theme.radii.md};
    font-family: inherit;
    font-size: 16px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    transition: background 0.15s;

    &.cancel_button {
      background: ${theme.colors.surface};
      border: 1.5px solid ${theme.colors.borderStrong};
      color: ${theme.colors.fg1};
      &:hover {
        background: ${theme.colors.gray100};
      }
    }

    &.remove_button {
      background: transparent;
      border: 1.5px solid ${theme.colors.danger};
      color: ${theme.colors.danger};
      &:hover {
        background: ${theme.colors.dangerSoft};
      }
    }

    &.confirm_button {
      background: ${theme.colors.danger};
      color: #fff;
      &:hover {
        background: #c73434;
      }
    }

    &.submit_button {
      background: ${theme.colors.primary};
      color: #fff;
      &:hover {
        background: ${theme.colors.primaryDark};
      }
    }

    &.add_param_button {
      width: 100%;
      margin-top: 12px;
      height: 48px;
      background: transparent;
      border: 1.5px dashed ${theme.colors.borderStrong};
      color: ${theme.colors.fg1};
      font-weight: 600;
      &:hover {
        background: ${theme.colors.tintOrange50};
        border-color: ${theme.colors.primary};
      }
    }
  }
`;
